import * as React from 'react'
import { IconCategory, IconType } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import { Input } from '@deepguide-ai/ui/input'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@deepguide-ai/ui/popover'
import { RadioGroup, RadioGroupItem } from '@deepguide-ai/ui/radio-group'
import { NodeSelection } from '@tiptap/pm/state'
import { Editor } from '@tiptap/react'
import * as Icons from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTopicAtomsContext } from '@/hooks/useTopicAtomsContext'
import { DocIcon } from '@/components/ui/doc-icon'
import { EmojiPicker } from '@/components/emoji-picker'
import { PageSearch } from '../link-popover/page-search'
import {
  SettingsField,
  SettingsPopoverHeader,
} from '../settings-popover/settings-popover'
import './button-floating-editor.scss'

interface ButtonFloatingEditorProps {
  editor: Editor
  isOpen: boolean
  onClose: () => void
}

export function ButtonFloatingEditor({
  editor,
  isOpen,
  onClose,
}: ButtonFloatingEditorProps) {
  const [mode, setMode] = React.useState<'edit' | 'search'>('edit')
  const [buttonText, setButtonText] = React.useState('New Button')
  const [variant, setVariant] = React.useState<'primary' | 'secondary'>(
    'primary'
  )
  const [selectedIcon, setSelectedIcon] = React.useState<string | null>(null)
  const [selectedEmoji, setSelectedEmoji] = React.useState<string | null>(null)
  const [iconCategory, setIconCategory] = React.useState<IconCategory>('LUCIDE')
  const [iconType, setIconType] = React.useState<IconType>('REGULAR')
  const [iconColor, setIconColor] = React.useState('#000000')
  const [href, setHref] = React.useState('')
  const [selectedPageTitle, setSelectedPageTitle] = React.useState<
    string | null
  >(null)
  const selectionRef = React.useRef<{ from: number; to: number } | null>(null)
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null)
  const { allTopicsFlat } = useTopicAtomsContext()
  const nodeRef = React.useRef<{ pos: number; node: any } | null>(null)
  const [selectedDocId, setSelectedDocId] = React.useState<string | null>(null)

  // Check if we're editing an existing button
  const isEditingExistingButton = React.useMemo(() => {
    if (!editor) return false
    const { selection } = editor.state
    const node = selection instanceof NodeSelection ? selection.node : null
    return node?.type.name === 'inlineButton'
  }, [editor])

  React.useEffect(() => {
    if (isOpen) {
      const checkAndMarkButton = () => {
        const { from, to } = editor.state.selection
        selectionRef.current = { from, to }

        const { selection } = editor.state
        const node = selection instanceof NodeSelection ? selection.node : null

        if (node?.type.name === 'inlineButton') {
          // Editing existing button node
          const { text, href, variant, icon } = node.attrs

          // Store the node position and reference for real-time updates
          nodeRef.current = { pos: from, node }

          // Load existing button attributes
          setButtonText(text || 'New Button')
          setVariant(variant || 'primary')
          setHref(href || '')

          // Handle icon/emoji
          if (icon) {
            if (icon.startsWith('emoji:')) {
              setSelectedEmoji(icon.replace('emoji:', ''))
              setSelectedIcon(null)
              setIconCategory('EMOJI')
            } else {
              setSelectedIcon(icon)
              setSelectedEmoji(null)
              setIconCategory('LUCIDE')
            }
          } else {
            // Clear icon selection if button has no icon
            setSelectedIcon(null)
            setSelectedEmoji(null)
          }

          // Find page title if it's an internal link
          if (href) {
            // const topicId = href.replace('/topics/', '')
            // const topic = allTopicsFlat.find((t) => t.id === topicId)
            const docId = href.split('?')[1]?.split('docId=')[1]?.split('&')[0]
            setSelectedDocId(docId || null)
            const topic = allTopicsFlat.find((t) => t.docId === docId)
            setSelectedPageTitle(topic?.title || null)
          }
        } else {
          // Creating new button from selected text
          const selectedText = editor.state.doc.textBetween(from, to, ' ')
          if (selectedText) {
            setButtonText(selectedText)
          } else {
            setButtonText('New Button')
          }
          // Reset other fields for new button
          setVariant('primary')
          setHref('')
          setSelectedIcon(null)
          setSelectedEmoji(null)
          setSelectedPageTitle(null)
        }
      }

      checkAndMarkButton()
      setTimeout(checkAndMarkButton, 100)
    }

    setMode(isEditingExistingButton ? 'edit' : 'edit')
  }, [isEditingExistingButton, isOpen, editor, allTopicsFlat])

  const getReferenceRect = React.useCallback(() => {
    if (!editor || !editor.state) return null

    const { from, to } = editor.state.selection
    const domRange = editor.view.domAtPos(from)
    const domRangeEnd = editor.view.domAtPos(to)

    const range = document.createRange()
    range.setStart(domRange.node, domRange.offset)
    range.setEnd(domRangeEnd.node, domRangeEnd.offset)

    return range.getBoundingClientRect()
  }, [editor])

  React.useEffect(() => {
    if (isOpen) {
      setAnchorRect(getReferenceRect())
    } else {
      setAnchorRect(null)
    }
  }, [isOpen, getReferenceRect])

  const handleSave = () => {
    // Use the button text as the content
    const content = buttonText || 'Button'

    // Determine the icon value based on selection
    let iconValue = ''
    if (selectedEmoji) {
      iconValue = `emoji:${selectedEmoji}`
    } else if (selectedIcon) {
      iconValue = selectedIcon
    }

    if (isEditingExistingButton && nodeRef.current) {
      // When editing existing button, the real-time updates have already changed it
      // Just ensure final state is saved
      const { pos } = nodeRef.current
      const node = editor.state.doc.nodeAt(pos)
      if (node && node.type.name === 'inlineButton') {
        editor
          .chain()
          .focus()
          .command(({ tr }) => {
            tr.setNodeMarkup(pos, null, {
              text: content,
              href: href || '',
              variant,
              icon: iconValue,
            })
            return true
          })
          .run()
      }
    } else {
      // For new button, insert at current position
      editor
        .chain()
        .focus()
        .deleteSelection()
        .insertInlineButton({
          text: content,
          href: href || '',
          variant,
          icon: iconValue,
        })
        .run()
    }

    onClose()
  }

  const handleRemove = () => {
    // For nodes, we need to delete the node
    if (selectionRef.current) {
      const { from, to } = selectionRef.current
      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .deleteSelection()
        .run()
    }

    onClose()
  }

  if (!isOpen || !editor || !anchorRect) {
    return null
  }

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor asChild>
        <span
          aria-hidden='true'
          className='pointer-events-none fixed h-px w-px'
          style={{ left: anchorRect.left, top: anchorRect.bottom }}
        />
      </PopoverAnchor>
      <PopoverContent
        align='start'
        side='bottom'
        sideOffset={10}
        className='w-80 p-4 pb-3'
      >
        {mode === 'search' ? (
          <PageSearch
            className='!w-full'
            initialQuery=''
            onSelect={(url, title) => {
              setHref(url)
              setSelectedDocId(
                url.split('?')[1]?.split('docId=')[1]?.split('&')[0] || null
              )
              setSelectedPageTitle(title || null)
              if (title && !isEditingExistingButton) {
                setButtonText(title)
              }

              // Update the button href in real-time if editing existing button
              if (isEditingExistingButton && nodeRef.current) {
                const { pos } = nodeRef.current
                const node = editor.state.doc.nodeAt(pos)
                if (node && node.type.name === 'inlineButton') {
                  // Use a transaction without focusing
                  const tr = editor.state.tr
                  tr.setNodeMarkup(pos, null, {
                    ...node.attrs,
                    href: url,
                  })
                  editor.view.dispatch(tr)
                }
              }

              setMode('edit')
            }}
            onBack={() => setMode('edit')}
          />
        ) : (
          <div className='button-editor'>
            <SettingsPopoverHeader title='Button Settings' onClose={onClose} />

            <div className='mt-4 space-y-4'>
              <SettingsField label='Label'>
                <div className='button-editor-label-row'>
                  <div className='button-editor-icon-picker'>
                    <EmojiPicker
                      selectedIcon={selectedEmoji || selectedIcon}
                      iconCategory={iconCategory}
                      iconType={iconType}
                      iconColor={iconColor}
                      isPopoverMode={true}
                      onIconChange={(iconName, category, type, color) => {
                        let iconValue = ''
                        if (category === 'EMOJI') {
                          setSelectedEmoji(iconName)
                          setSelectedIcon(null)
                          setIconCategory('EMOJI')
                          iconValue = `emoji:${iconName}`
                        } else {
                          setSelectedIcon(iconName)
                          setSelectedEmoji(null)
                          setIconCategory(category)
                          if (type) setIconType(type)
                          if (color) setIconColor(color)
                          iconValue = iconName
                        }

                        // Update the button icon in real-time if editing existing button
                        if (isEditingExistingButton && nodeRef.current) {
                          const { pos } = nodeRef.current
                          const node = editor.state.doc.nodeAt(pos)
                          if (node && node.type.name === 'inlineButton') {
                            // Use a transaction without focusing
                            const tr = editor.state.tr
                            tr.setNodeMarkup(pos, null, {
                              ...node.attrs,
                              icon: iconValue,
                            })
                            editor.view.dispatch(tr)
                          }
                        }
                      }}
                      onEmojiSelect={(emoji) => {
                        setSelectedEmoji(emoji)
                        setSelectedIcon(null)
                        setIconCategory('EMOJI')

                        // Update the button icon in real-time if editing existing button
                        if (isEditingExistingButton && nodeRef.current) {
                          const { pos } = nodeRef.current
                          const node = editor.state.doc.nodeAt(pos)
                          if (node && node.type.name === 'inlineButton') {
                            // Use a transaction without focusing
                            const tr = editor.state.tr
                            tr.setNodeMarkup(pos, null, {
                              ...node.attrs,
                              icon: `emoji:${emoji}`,
                            })
                            editor.view.dispatch(tr)
                          }
                        }
                      }}
                    />
                  </div>

                  <Input
                    value={buttonText}
                    onChange={(e) => {
                      const newText = e.target.value
                      setButtonText(newText)

                      // Update the button in real-time if editing existing button
                      if (isEditingExistingButton && nodeRef.current) {
                        const { pos } = nodeRef.current
                        const node = editor.state.doc.nodeAt(pos)
                        if (node && node.type.name === 'inlineButton') {
                          // Use a transaction without focusing
                          const tr = editor.state.tr
                          tr.setNodeMarkup(pos, null, {
                            ...node.attrs,
                            text: newText || 'Button',
                          })
                          editor.view.dispatch(tr)
                        }
                      }
                    }}
                    placeholder='Button text'
                    className='button-editor-input'
                  />
                </div>
              </SettingsField>

              <SettingsField
                label='Variant'
                description="The button will use your site's customization settings."
              >
                <div className='bg-muted inline-flex h-9 w-full rounded-md p-1'>
                  <RadioGroup
                    value={variant}
                    className='group has-focus-visible:after:border-ring has-focus-visible:after:ring-ring/50 relative inline-grid w-full grid-cols-[1fr_1fr] items-center gap-0 text-sm font-medium after:absolute after:inset-y-0 after:w-1/2 after:rounded-sm after:bg-card after:shadow-xs after:transition-[translate,box-shadow] after:duration-300 after:ease-[cubic-bezier(0.16,1,0.3,1)] has-focus-visible:after:ring-[3px] data-[state=primary]:after:translate-x-0 data-[state=secondary]:after:translate-x-full'
                    onValueChange={(v) => {
                      const newVariant = v as 'primary' | 'secondary'
                      setVariant(newVariant)

                      // Update the button variant in real-time if editing existing button
                      if (isEditingExistingButton && nodeRef.current) {
                        const { pos } = nodeRef.current
                        const node = editor.state.doc.nodeAt(pos)
                        if (node && node.type.name === 'inlineButton') {
                          // Use a transaction without focusing
                          const tr = editor.state.tr
                          tr.setNodeMarkup(pos, null, {
                            ...node.attrs,
                            variant: newVariant,
                          })
                          editor.view.dispatch(tr)
                        }
                      }
                    }}
                    data-state={variant}
                  >
                    <label className='group-data-[state=secondary]:text-muted-foreground/70 relative z-10 inline-flex h-full min-w-8 cursor-pointer items-center justify-center px-2 py-1 text-sm leading-5 font-medium whitespace-nowrap transition-colors select-none'>
                      Primary
                      <RadioGroupItem
                        value='primary'
                        id='primary'
                        className='sr-only'
                      />
                    </label>
                    <label className='group-data-[state=primary]:text-muted-foreground/70 relative z-10 inline-flex h-full min-w-8 cursor-pointer items-center justify-center px-2 py-1 text-sm leading-5 font-medium whitespace-nowrap transition-colors select-none'>
                      Secondary
                      <RadioGroupItem
                        id='secondary'
                        value='secondary'
                        className='sr-only'
                      />
                    </label>
                  </RadioGroup>
                </div>
              </SettingsField>

              <SettingsField
                label='On click'
                description={
                  'Your button can point to either a URL, or a page or space in GitBook.'
                }
              >
                <div
                  className='cursor-pointer rounded-md border px-2 py-1 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]'
                  onClick={() => setMode('search')}
                >
                  {href ? (
                    <div className='button-editor-link-content h-8'>
                      {selectedPageTitle && selectedDocId ? (
                        <>
                          <DocIcon
                            topic={
                              allTopicsFlat.find(
                                (t) => t.docId === selectedDocId
                              )!
                            }
                            size={16}
                            className='flex-shrink-0'
                          />
                          <span className='button-editor-link-text'>
                            {selectedPageTitle}
                          </span>
                        </>
                      ) : (
                        <>
                          <Icons.Globe className='text-muted-foreground h-4 w-4 flex-shrink-0' />
                          <span className='button-editor-link-text'>
                            {href}
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className='text-muted-foreground flex h-8 w-full items-center gap-2 text-sm leading-5'>
                      <Icons.Link className='h-4 w-4 flex-shrink-0' />
                      Select page or URL
                    </div>
                  )}
                </div>
              </SettingsField>
            </div>

            <div className='mt-4 flex w-full items-center justify-between border-t pt-3'>
              {isEditingExistingButton && (
                <Button
                  variant='ghost'
                  size='smIcon'
                  onClick={handleRemove}
                  className='text-destructive hover:text-destructive/80 hover:bg-destructive/10'
                >
                  <Icons.Trash2 className='h-4 w-4' />
                </Button>
              )}
              <div className='button-editor-actions'>
                <Button variant='outline' onClick={onClose} size='sm'>
                  Cancel
                </Button>
                <Button onClick={handleSave} size='sm'>
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
